import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';

import type {
  AboutPageDefaultContentTemplateDynamicZone,
  ComponentCommonDownloadableContentList,
  ComponentCommonTextEditor,
  Maybe,
  KnowledgeBaseDefaultContentTemplateDynamicZone,
  KnowledgeBase,
  ResourceDefaultContentTemplateDynamicZone,
  PodcastDefaultContentTemplateDynamicZone,
} from '@edwin-group/people-graphql';

import DefaultContentTemplateSkeleton from './DefaultContentTemplate.skeleton';
import DefaultContentTemplateBullets from './Bullets';
import DefaultContentTemplateQuote from './Quote';
import DefaultContentTemplateFaq from './Faq';
import DefaultContentTemplateAuthorDetailed from './AuthorDetailed';
import DefaultContentTemplateTags from './Tags';
import DefaultContentTemplateRelatedContent, { RelatedContent } from './RelatedContent';
import Divider from '../Divider';

import DefaultContentTemplateH3 from './H3';
import DefaultContentTemplateTitle from './Title';
import DefaultContentTemplateTextEditor from './TextEditor';
import DefaultContentTemplateMedia from './Media';
import DefaultContentTemplateAuthor from './Author';
import DefaultContentTemplateEmbedded from './Embedded';
import DefaultContentTemplateDownloadableContent from './DownloadableContent';

import './index.css';
import DefaultContentTemplateAudio, { ListeningTimeRefProps } from './Audio';
import Timer from './Timer';
import KnowledgeBaseCard from '../Card/KnowledgeBase';
import { ResourceAssets, ResourceAssetItem, ResourceInfo } from './ResourceAssets';
export interface ComponentCommonTextEditorWithHtml extends ComponentCommonTextEditor {
  markdownHtml: string;
}

export type ContentProps =
  | Array<Maybe<KnowledgeBaseDefaultContentTemplateDynamicZone>>
  | Array<Maybe<AboutPageDefaultContentTemplateDynamicZone>>
  | Array<Maybe<ResourceDefaultContentTemplateDynamicZone>>
  | Array<Maybe<PodcastDefaultContentTemplateDynamicZone>>
  | null;

type DefaultContentTemplateProps = {
  loading?: boolean;
  content?: ContentProps;
  tags?: string[];
  relatedContent?: RelatedContent[];
  knowledgebasesRelated?: KnowledgeBase[];
  fitContent?: boolean;
  handleTimeUpdate?: (audio: HTMLAudioElement) => void;
  listeningTime?: ListeningTimeRefProps;
  idBodyDivider?: boolean;
  downloadableContentTitle?: string;
  onPlayAudio?: () => void;
  onVideoPlay?: () => void;
  meditationCard?: {
    image: string;
  };
  resourceHub?: {
    resource: ResourceInfo;
    onPreview?: (asset: ResourceAssetItem | ResourceAssetItem[]) => void;
    assetPreviewStatuses?: Record<string, ResourceAssetItem['previewStatus']>;
    downloadButtonText?: string;
    previewButtonText?: string;
    downloadAllButtonText?: string;
  };
  hideEmptyState?: boolean;
};

function DefaultContentTemplate({
  content = [],
  loading,
  tags,
  relatedContent,
  knowledgebasesRelated,
  fitContent,
  listeningTime,
  handleTimeUpdate,
  downloadableContentTitle,
  onPlayAudio,
  onVideoPlay,
  resourceHub,
  hideEmptyState,
}: DefaultContentTemplateProps) {
  if (loading) {
    return <DefaultContentTemplateSkeleton />;
  }

  if (content?.length === 0) {
    if (hideEmptyState) return null;
    return <h3 className="my-sh-30 font-haffer text-sh-24">No content available</h3>;
  }

  return (
    <div className="default-content-template no-last-gap flex flex-col gap-y-sh-30 !font-haffer">
      {content?.map((c, idx) => {
        if (c?.__typename === 'ComponentCommonDivider') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <Divider key={c.id} />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonH3') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateH3 key={c.id} component={c} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonTitle') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateTitle key={c.id} component={c} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonTextEditor') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateTextEditor
                  key={c.id}
                  component={c as ComponentCommonTextEditorWithHtml}
                  isDefaultContentTemplate
                />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonDownloadableContentList') {
          return (
            <>
              <DefaultContentTemplateDownloadableContent
                key={c.id}
                component={c as ComponentCommonDownloadableContentList}
                title={downloadableContentTitle}
                isDefaultContentTemplate
              />
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonMedia') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateMedia
                  key={c.id}
                  component={c}
                  className={fitContent ? '' : 'lg:-mx-[12%]'}
                  isDefaultContentTemplate
                />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (
          c?.__typename === 'ComponentCommonAuthor' &&
          (!!c?.publicationDate || !!c?.author?.data?.attributes?.name)
        ) {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateAuthor key={c.id} component={c} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonEmbedded') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateEmbedded
                  onVideoPlay={onVideoPlay}
                  key={c.id}
                  component={c}
                  fitContent
                  isDefaultContentTemplate
                />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonBullet') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateBullets key={c.id} component={c} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonQuote') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateQuote key={c.id} component={c} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonFaqComponent') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateFaq key={c.id} component={c} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonAuthorDetailed') {
          return (
            <AnimatedFadeInComponent key={c.id}>
              <DefaultContentTemplateAuthorDetailed key={c.id} component={c} />
            </AnimatedFadeInComponent>
          );
        }

        if (c?.__typename === 'ComponentCommonTags') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateTags key={c.id} tags={tags || []} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonRelatedContent') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateRelatedContent
                  key={c.id}
                  relatedContent={relatedContent || []}
                  isDefaultContentTemplate
                />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentKnowledgeBaseKnowledgeBaseRelated') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id as unknown as string}>
                <h2 className="mb-sh-30 text-sh-24 font-bold">Related content</h2>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {knowledgebasesRelated?.map((knowledgebase) => (
                    <KnowledgeBaseCard knowledgebase={knowledgebase} key={knowledgebase?.title} />
                  ))}
                </div>
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonAudio') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateAudio
                  key={c.id}
                  audio={c}
                  onPlayAudio={onPlayAudio}
                  handleTimeUpdate={handleTimeUpdate}
                  listeningTime={listeningTime}
                  isDefaultContentTemplate
                />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentDefaultContentTemplateTimer') {
          return <Timer key={c.id || `Timer-${idx}`} component={c} />;
        }

        if (c?.__typename === 'ComponentResourceAssets' && resourceHub) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const component = c as any;
          const assets = (component?.assets?.nodes || [])
            .map((asset: unknown) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const a = asset as any;
              const documentId = a.documentId;
              return {
                documentId,
                title: a.title,
                description: a.description,
                publishedAt: a.publishedAt,
                updatedAt: a.updatedAt,
                previewStatus: resourceHub.assetPreviewStatuses?.[documentId] ?? a.previewStatus,
                scanStatus: a.scanStatus,
                content: a.content,
                resourceAssetHistory: a.resourceAssetHistory?.nodes || null,
              } as ResourceAssetItem;
            })
            .filter((asset: ResourceAssetItem) => asset.scanStatus !== 'processing' && asset.scanStatus !== 'infected');

          if (assets.length === 0) return null;

          const resourceAssetsCount =
            content?.filter((item) => item?.__typename === 'ComponentResourceAssets').length || 0;

          return (
            <AnimatedFadeInComponent key={component.id || `resource-assets-${idx}`}>
              <ResourceAssets
                assets={assets}
                resource={resourceHub.resource}
                onPreview={resourceHub.onPreview}
                totalSections={resourceAssetsCount}
                sectionTitle={component?.sectionTitle}
                downloadButtonText={resourceHub.downloadButtonText}
                previewButtonText={resourceHub.previewButtonText}
                downloadAllButtonText={resourceHub.downloadAllButtonText}
              />
            </AnimatedFadeInComponent>
          );
        }

        return null;
      })}
    </div>
  );
}

export default DefaultContentTemplate;
